import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  Chip,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Stack,
  CircularProgress,
  Switch,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getFirestore, collection, query, where, getDocs, getDoc, orderBy as fbOrderBy, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import Skeleton from '@mui/material/Skeleton';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import firebaseApp from '../../utils/firebaseConfig';
import { formatPhoneDisplay, phoneDigits } from '../../utils/phone';
import { pickBestMember, isSamePerson } from '../../utils/members';
import { stripAccents } from '../../utils/search';
import { CALIBER_OPTIONS } from '../../utils/calibers';
import { isPrivateCourse, COURSE_LANGUAGE_OPTIONS, LICENSE_TYPE_OPTIONS } from '../../utils/privateCourse';

const db = getFirestore(firebaseApp);
const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';

export { CALIBER_OPTIONS };
const STRIPE_PK = 'pk_live_51Q5pO5RqULsyaFnGO4c7JjHvqYv9Eu4FovYOlgCfuWZyrpYgn33p1cZavi28z41Nxpo3PiKLbscqL7c2ezQUFux400HaYeh7o6';

const steps = ['Tipo', 'Categoría', 'Fecha', 'Horario', 'Confirmar'];

const SHOOTING_TYPES = ['lane', 'no-license-shooting'];
const COURSE_TYPES = ['course'];

const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#F8F9FA',
});

const Header = styled(Box)({
  backgroundColor: '#333',
  color: '#FFF',
  padding: '20px 0',
  marginBottom: 32,
});

const defaultImages = {
  lane: 'https://images.unsplash.com/photo-1595590424283-b8f17842773f?w=800&q=80',
  course: 'https://images.unsplash.com/photo-1619123707367-bb6647e1a720?w=800&q=80',
  class: 'https://images.unsplash.com/photo-1615840287214-7ff58936c4cf?w=800&q=80',
  event: 'https://images.unsplash.com/photo-1548345680-f5475ea5df84?w=800&q=80',
};

const typeLabels = { lane: 'Lane de Tiro', course: 'Curso', class: 'Clase', event: 'Evento', 'no-license-shooting': 'Tiro Sin Licencia' };

export default function MakeReservationPage() {
  const [activeStep, setActiveStep] = useState(0);
  const [loginInput, setLoginInput] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userData, setUserData] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Step data
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPM, setSelectedPM] = useState('');
  const [stripeCustomerId, setStripeCustomerId] = useState(null);

  // Datos del cliente para confirmación (se prellena si está en sistema)
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerLicense, setCustomerLicense] = useState('');
  const [infoExpanded, setInfoExpanded] = useState(true);

  // PIN verification
  const [pinVerified, setPinVerified] = useState(false);
  const [pinStep, setPinStep] = useState(null); // 'verify' | 'create'
  const [pinValue, setPinValue] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [pinIdentifier, setPinIdentifier] = useState('');
  // Olvidé mi PIN (email con link único que expira)
  const [pinResetSent, setPinResetSent] = useState(false);
  const [pinResetLoading, setPinResetLoading] = useState(false);

  const handleForgotPin = async () => {
    setPinResetLoading(true);
    setPinError('');
    try {
      await axios.post(`${FUNCTIONS_BASE}/requestPinReset`, { identifier: pinIdentifier });
      setPinResetSent(true);
    } catch (e) {
      setPinError('No se pudo enviar el email. Intenta de nuevo.');
    }
    setPinResetLoading(false);
  };

  // Cambiar PIN estando autenticado — actualiza user-pins (web) y members.pin (app)
  const [changePinOpen, setChangePinOpen] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [newPinConfirm, setNewPinConfirm] = useState('');
  const [changePinSaving, setChangePinSaving] = useState(false);
  const [changePinError, setChangePinError] = useState('');
  const [changePinDone, setChangePinDone] = useState(false);

  const handleChangePin = async () => {
    setChangePinError('');
    if (newPin.length !== 4) { setChangePinError('El PIN debe ser de 4 dígitos.'); return; }
    if (newPin !== newPinConfirm) { setChangePinError('Los PINs no coinciden.'); return; }
    setChangePinSaving(true);
    try {
      const dbRef = getFirestore();
      const ids = [...new Set([
        (userEmail || '').toLowerCase().trim(),
        (userData?.phone || '').trim(),
        (pinIdentifier || '').trim(),
      ].filter(Boolean))];
      await Promise.all(ids.map((i) => setDoc(doc(dbRef, 'user-pins', i), { pin: newPin, updatedAt: serverTimestamp() }, { merge: true })));
      if (userData?.id) {
        await setDoc(doc(dbRef, 'members', userData.id), { pin: newPin }, { merge: true });
      }
      setChangePinDone(true);
      setTimeout(() => {
        setChangePinOpen(false);
        setChangePinDone(false);
        setNewPin('');
        setNewPinConfirm('');
      }, 1500);
    } catch (e) {
      console.error(e);
      setChangePinError('No se pudo cambiar el PIN. Intenta de nuevo.');
    }
    setChangePinSaving(false);
  };

  const [bookingType, setBookingType] = useState(null); // 'shooting' or 'courses'
  const [categoryFilter, setCategoryFilter] = useState('');

  // Calibres que usará el día de la reserva (multi-select + "Otro")
  const [selectedCalibers, setSelectedCalibers] = useState([]);
  // Curso privado: idioma y nueva/renovación (obligatorios en esa categoría)
  const [courseLanguage, setCourseLanguage] = useState('');
  const [licenseType, setLicenseType] = useState('');
  const [otherCaliber, setOtherCaliber] = useState('');
  const toggleCaliber = (cal) => {
    setSelectedCalibers((prev) => (prev.includes(cal) ? prev.filter((c) => c !== cal) : [...prev, cal]));
  };

  const [addingCard, setAddingCard] = useState(false);
  const [cardElementReady, setCardElementReady] = useState(false);
  const [addCardError, setAddCardError] = useState('');
  const [stripeInstance, setStripeInstance] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const [setupClientSecret, setSetupClientSecret] = useState('');

  // Tarjeta nueva SIEMPRE visible en el paso de pago. El toggle "No guardar
  // esta tarjeta" (apagado por defecto) decide si se guarda: apagado = se
  // guarda al pagar (setup_future_usage), prendido = solo este pago.
  const [onceCard, setOnceCard] = useState(null);
  const [onceCardReady, setOnceCardReady] = useState(false);
  const [onceCardComplete, setOnceCardComplete] = useState(false);
  const [dontSaveCard, setDontSaveCard] = useState(false);

  // Campos de tarjeta SEPARADOS (número / mes-año / CVC). El element 'card'
  // de una sola línea no cabe en pantallas angostas (Android ~360px, y menos
  // dentro del WebView de la app): Stripe encogía los campos hasta que
  // "MM / AA", "CVC" y "Código postal" se dibujaban uno encima del otro y el
  // cliente no podía escribir la fecha. Separados no se pueden solapar.
  const CARD_EL_STYLE = {
    base: { fontSize: '16px', color: '#333', '::placeholder': { color: '#999' } },
    invalid: { color: '#d40100' },
  };

  const mountOnceCard = async () => {
    if (onceCard) {
      // El contenedor puede no haber existido (p. ej. total $0 que luego
      // subió con acompañante) — reintentar solo el mount
      if (!onceCardReady) {
        setTimeout(() => {
          if (document.getElementById('stripe-card-number')) {
            try {
              onceCard.number.mount('#stripe-card-number');
              onceCard.expiry.mount('#stripe-card-expiry');
              onceCard.cvc.mount('#stripe-card-cvc');
              setOnceCardReady(true);
            } catch (e) { /* ya montado */ }
          }
        }, 150);
      }
      return;
    }
    try {
      const stripeObj = stripeInstance || await loadStripe(STRIPE_PK);
      setStripeInstance(stripeObj);
      const elements = stripeObj.elements();
      const number = elements.create('cardNumber', { style: CARD_EL_STYLE, placeholder: '1234 1234 1234 1234' });
      const expiry = elements.create('cardExpiry', { style: CARD_EL_STYLE });
      const cvc = elements.create('cardCvc', { style: CARD_EL_STYLE });

      // La tarjeta está lista solo cuando los tres campos están completos
      const parts = { number: false, expiry: false, cvc: false };
      const watch = (key, el) => el.on('change', (ev) => {
        parts[key] = ev.complete === true;
        setOnceCardComplete(parts.number && parts.expiry && parts.cvc);
        // Si empieza a escribir una tarjeta nueva, se des-selecciona la guardada
        if (!ev.empty) setSelectedPM('');
      });
      watch('number', number);
      watch('expiry', expiry);
      watch('cvc', cvc);

      setOnceCard({ number, expiry, cvc });
      setTimeout(() => {
        if (document.getElementById('stripe-card-number')) {
          number.mount('#stripe-card-number');
          expiry.mount('#stripe-card-expiry');
          cvc.mount('#stripe-card-cvc');
          setOnceCardReady(true);
        }
      }, 150);
    } catch (e) {
      console.error(e);
    }
  };


  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [error, setError] = useState('');

  // Check if user was previously logged in
  useEffect(() => {
    const saved = localStorage.getItem('reservation_user');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.email) setUserEmail(parsed.email);
        setUserData(parsed.id ? parsed : null);
        setIsMember(parsed.active === true);
        setIsLoggedIn(true);
        setPinVerified(true);
        if (parsed.fullName) setCustomerName(parsed.fullName);
        if (parsed.phone) setCustomerPhone(parsed.phone);
        if (parsed.licenseNumber) setCustomerLicense(parsed.licenseNumber);
        if (parsed.fullName && parsed.phone) setInfoExpanded(false);
        if (parsed.email) loadPaymentMethods(parsed.email);
      } catch (e) { /* ignore */ }
    }
  }, []);

  // Cuando se actualiza userData (e.g. tras login), prellenamos datos del cliente
  useEffect(() => {
    if (userData) {
      if (userData.fullName) setCustomerName(userData.fullName);
      if (userData.phone) setCustomerPhone(userData.phone);
      if (userData.licenseNumber) setCustomerLicense(userData.licenseNumber);
      const hasName = !!(userData.fullName);
      const hasPhone = !!(userData.phone);
      setInfoExpanded(!(hasName && hasPhone));
    }
  }, [userData]);

  // Try to recover user details from past reservations when not found in members
  const lookupPastReservationData = async (email, phone) => {
    try {
      let snap;
      if (email) {
        const q = query(collection(db, 'reservations'), where('userEmail', '==', email.toLowerCase()), fbOrderBy('createdAt', 'desc'));
        snap = await getDocs(q);
      }
      if ((!snap || snap.empty) && phone) {
        const q = query(collection(db, 'reservations'), where('userPhone', '==', phone), fbOrderBy('createdAt', 'desc'));
        snap = await getDocs(q);
      }
      if (snap && !snap.empty) {
        const r = snap.docs[0].data();
        return {
          fullName: r.userName || '',
          phone: r.userPhone || '',
          email: r.userEmail || '',
          licenseNumber: r.licenseNumber || '',
        };
      }
    } catch (e) {
      console.error('Error looking up past reservations:', e);
    }
    return null;
  };

  const normalizePhone = (raw) => raw.replace(/\D/g, '');

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim());

  const isPhoneInput = (val) => {
    const digits = val.replace(/\D/g, '');
    return digits.length >= 7 && /^\d+$/.test(digits);
  };

  const handleLogin = async () => {
    const raw = loginInput.trim();
    if (!raw) { setLoginError('Ingresa tu email o número de teléfono'); return; }
    setLoginLoading(true);
    setLoginError('');
    try {
      let snap;
      let lookupEmail = '';
      let lookupPhone = '';

      if (isPhoneInput(raw)) {
        lookupPhone = normalizePhone(raw);
        const q = query(collection(db, 'members'), where('phone', '==', lookupPhone));
        snap = await getDocs(q);
      } else {
        lookupEmail = raw.toLowerCase();
        const q = query(collection(db, 'members'), where('email', '==', lookupEmail));
        snap = await getDocs(q);
      }

      if (snap.empty) {
        // Not in members — try to recover info from past reservations
        const pastData = await lookupPastReservationData(lookupEmail, lookupPhone);
        setUserData(null);
        setIsMember(false);
        setIsLoggedIn(true);

        const stored = {};
        if (lookupPhone) {
          setCustomerPhone(lookupPhone);
          stored.phone = lookupPhone;
        }
        if (lookupEmail) {
          setUserEmail(lookupEmail);
          stored.email = lookupEmail;
        }
        if (pastData) {
          if (pastData.fullName) { setCustomerName(pastData.fullName); stored.fullName = pastData.fullName; }
          if (pastData.phone && !lookupPhone) { setCustomerPhone(pastData.phone); stored.phone = pastData.phone; }
          if (pastData.email && !lookupEmail) { setUserEmail(pastData.email); stored.email = pastData.email; }
          if (pastData.licenseNumber) { setCustomerLicense(pastData.licenseNumber); stored.licenseNumber = pastData.licenseNumber; }
        }
        localStorage.setItem('reservation_user', JSON.stringify(stored));
        if (stored.email) await loadPaymentMethods(stored.email);
        const pinId1 = lookupPhone || lookupEmail;
        setPinIdentifier(pinId1);
        try {
          const pinSnap = await getDoc(doc(db, 'user-pins', pinId1));
          setPinStep(pinSnap.exists() ? 'verify' : 'create');
        } catch (e) { setPinVerified(true); }
        setLoginLoading(false);
        return;
      }
      // Cuentas duplicadas: escoge siempre la misma —y la correcta— en vez
      // del primer doc que devuelva Firestore (reporte 2026-09-10)
      const member = pickBestMember(snap.docs, { email: lookupEmail, phone: lookupPhone });
      setUserData(member);
      setIsMember(member.active === true);
      setIsLoggedIn(true);
      if (member.email) setUserEmail(member.email);
      if (member.phone) setCustomerPhone(member.phone);
      localStorage.setItem('reservation_user', JSON.stringify({ ...member, email: member.email || lookupEmail }));
      if (member.email) await loadPaymentMethods(member.email);
      fetchCategories();
      const pinId2 = lookupPhone || lookupEmail;
      setPinIdentifier(pinId2);
      try {
        const pinSnap2 = await getDoc(doc(db, 'user-pins', pinId2));
        setPinStep(pinSnap2.exists() ? 'verify' : 'create');
      } catch (e) { setPinVerified(true); }
    } catch (err) {
      setLoginError('Error al verificar tu cuenta');
    }
    setLoginLoading(false);
  };

  const handlePinVerify = async () => {
    if (pinValue.length !== 4) { setPinError('El PIN debe ser de 4 dígitos'); return; }
    setPinLoading(true);
    setPinError('');
    try {
      const pinSnap = await getDoc(doc(db, 'user-pins', pinIdentifier));
      if (pinSnap.exists() && pinSnap.data().pin === pinValue) {
        setPinVerified(true);
        setPinStep(null);
      } else {
        setPinError('PIN incorrecto. Intenta de nuevo.');
        setPinValue('');
      }
    } catch (e) {
      setPinError('Error al verificar el PIN');
    }
    setPinLoading(false);
  };

  const handlePinCreate = async () => {
    if (pinValue.length !== 4) { setPinError('El PIN debe ser de 4 dígitos'); return; }
    if (pinValue !== pinConfirm) { setPinError('Los PINs no coinciden. Intenta de nuevo.'); setPinConfirm(''); return; }
    setPinLoading(true);
    setPinError('');
    try {
      await setDoc(doc(db, 'user-pins', pinIdentifier), {
        pin: pinValue,
        createdAt: serverTimestamp(),
      });
      setPinVerified(true);
      setPinStep(null);
    } catch (e) {
      setPinError('Error al guardar el PIN');
    }
    setPinLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('reservation_user');
    setIsLoggedIn(false);
    setUserData(null);
    setUserEmail('');
    setLoginInput('');
    setActiveStep(0);
    setBookingType(null);
    setSelectedCategory(null);
    setSelectedDate('');
    setPinVerified(false);
    setPinStep(null);
    setPinValue('');
    setPinConfirm('');
    setPinError('');
    setPinIdentifier('');
    setSelectedSlot(null);
    setBookingResult(null);
  };

  const loadPaymentMethods = async (email) => {
    try {
      const hapQ = query(collection(db, 'hap-members'), where('email', '==', email));
      const hapSnap = await getDocs(hapQ);
      if (!hapSnap.empty) {
        const hap = hapSnap.docs[0].data();
        if (hap.stripeCustomerId) {
          setStripeCustomerId(hap.stripeCustomerId);
          const res = await axios.post(`${FUNCTIONS_BASE}/listPaymentMethods`, { customerId: hap.stripeCustomerId });
          if (res.data.paymentMethods) {
            setPaymentMethods(res.data.paymentMethods);
            if (res.data.paymentMethods.length > 0) {
              setSelectedPM(res.data.paymentMethods[0].id);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error loading payment methods:', err);
    }
  };

  const handleStartAddCard = async () => {
    setAddingCard(true);
    setAddCardError('');
    try {
      const email = userEmail || null;
      const res = await axios.post(`${FUNCTIONS_BASE}/createSetupIntent`, {
        email,
        customerId: stripeCustomerId || undefined,
      });
      const { clientSecret, customer } = res.data;
      setSetupClientSecret(clientSecret);
      if (customer) setStripeCustomerId(customer);

      const stripeObj = await loadStripe(STRIPE_PK);
      setStripeInstance(stripeObj);

      const elements = stripeObj.elements({ clientSecret });
      const card = elements.create('card', {
        style: {
          base: { fontSize: '16px', color: '#333', '::placeholder': { color: '#999' } },
          invalid: { color: '#d40100' },
        },
      });
      setCardElement(card);
      setTimeout(() => {
        const container = document.getElementById('stripe-card-element');
        if (container) {
          card.mount('#stripe-card-element');
          setCardElementReady(true);
        }
      }, 100);
    } catch (err) {
      console.error('Error starting add card:', err);
      setAddCardError('Error al iniciar. Intenta de nuevo.');
    }
  };

  const handleConfirmAddCard = async () => {
    if (!stripeInstance || !cardElement || !setupClientSecret) return;
    setAddCardError('');
    setProcessing(true);
    try {
      const { error, setupIntent } = await stripeInstance.confirmCardSetup(setupClientSecret, {
        payment_method: { card: cardElement },
      });
      if (error) {
        setAddCardError(error.message);
        setProcessing(false);
        return;
      }
      if (setupIntent.status === 'succeeded') {
        cardElement.unmount();
        setAddingCard(false);
        setCardElementReady(false);
        setCardElement(null);
        setSetupClientSecret('');
        // Reload payment methods
        const custId = stripeCustomerId || setupIntent.customer;
        if (custId) {
          setStripeCustomerId(custId);
          const pmRes = await axios.post(`${FUNCTIONS_BASE}/listPaymentMethods`, { customerId: custId });
          if (pmRes.data.paymentMethods) {
            setPaymentMethods(pmRes.data.paymentMethods);
            if (pmRes.data.paymentMethods.length > 0) {
              setSelectedPM(pmRes.data.paymentMethods[pmRes.data.paymentMethods.length - 1].id);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error confirming card:', err);
      setAddCardError('Error al guardar la tarjeta. Intenta de nuevo.');
    }
    setProcessing(false);
  };

  const handleCancelAddCard = () => {
    if (cardElement) {
      try { cardElement.unmount(); } catch (e) { /* ignore */ }
    }
    setAddingCard(false);
    setCardElementReady(false);
    setCardElement(null);
    setSetupClientSecret('');
    setAddCardError('');
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'reservation_categories'), where('active', '==', true), fbOrderBy('sortOrder', 'asc'));
      const snap = await getDocs(q);
      // Las categorías ocultas al público solo se inscriben desde el dashboard
      setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.hiddenFromPublic !== true));
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isLoggedIn && categories.length === 0) fetchCategories();
  }, [isLoggedIn]);

  // laneId opcional: cuando el cliente ya escogió lane, el backend devuelve
  // solo la disponibilidad de ese lane.
  const fetchSlots = async (catId, date, laneId = '') => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${FUNCTIONS_BASE}/getAvailableSlots`, {
        categoryId: catId,
        date,
        laneId: laneId || null,
      });
      setSlots(res.data.slots || []);
    } catch (err) {
      setError('Error al cargar horarios disponibles');
    }
    setLoading(false);
  };

  const handleSelectBookingType = (type) => {
    setBookingType(type);
    setCategoryFilter('');
    setSelectedCategory(null);
    setSelectedDate('');
    setSelectedSlot(null);
    setSlots([]);
    setActiveStep(1);
  };

  const handleSelectCategory = (cat) => {
    setSelectedCategory(cat);
    setSelectedDate('');
    setSelectedSlot(null);
    setSlots([]);
    // El acompañante/lane/cupón dependen de la categoría — se limpian al cambiarla
    resetCompanion();
    resetForOther();
    resetCoupon();
    setSelectedLaneId('');
    setAvailableLanes([]);
    setActiveStep(2);
  };

  const handleSelectDate = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setSelectedLaneId('');
    setAvailableLanes([]);
    fetchSlots(selectedCategory.id, date);
    // El selector de lane vive en el paso de Horario
    if (selectedCategory?.type === 'lane') loadCategoryLanes(selectedCategory.id);
    setActiveStep(3);
  };

  // --- Selección de lane (opcional; vacío = asignación automática) ---
  const [availableLanes, setAvailableLanes] = useState([]);
  const [lanesLoading, setLanesLoading] = useState(false);
  const [selectedLaneId, setSelectedLaneId] = useState('');

  // Lanes de la categoría. No traen disponibilidad: esa se ve en los horarios,
  // que se recalculan para el lane escogido.
  const loadCategoryLanes = async (catId) => {
    setLanesLoading(true);
    try {
      const dbRef = getFirestore();
      const snap = await getDocs(query(
        collection(dbRef, 'lanes'),
        where('categoryIds', 'array-contains', catId),
        where('active', '==', true),
      ));
      const list = snap.docs.map((d) => ({ id: d.id, name: d.data().name }));
      const num = (l) => {
        const m = String(l.name || '').match(/\d+/);
        return m ? parseInt(m[0], 10) : 999;
      };
      list.sort((a, b) => num(a) - num(b) || String(a.name).localeCompare(String(b.name)));
      setAvailableLanes(list);
    } catch (err) {
      // Si falla, el cliente sigue pudiendo reservar con asignación automática
      setAvailableLanes([]);
    }
    setLanesLoading(false);
  };

  // Cambiar de lane recarga los horarios: cada lane tiene su disponibilidad
  const handleSelectLane = (laneId) => {
    setSelectedLaneId(laneId);
    setSelectedSlot(null);
    if (selectedCategory && selectedDate) {
      fetchSlots(selectedCategory.id, selectedDate, laneId);
    }
  };

  const handleSelectSlot = (slot) => {
    setSelectedSlot(slot);
    setActiveStep(4);
  };

  // --- Reservación para otra persona (invitado) ---
  const [forOther, setForOther] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestLicense, setGuestLicense] = useState('');
  const [guestIsMember, setGuestIsMember] = useState(null); // null = sin verificar
  const [guestChecking, setGuestChecking] = useState(false);
  // Flujo "teléfono primero" (pedido 2026-09-15): se busca por teléfono; si
  // existe se prellenan sus datos y se muestran como resumen; si no, ahí sí
  // se piden nombre / email / licencia.
  const [guestLookup, setGuestLookup] = useState('idle'); // idle | found | not_found
  const [guestEditing, setGuestEditing] = useState(false);

  // Busca al invitado por TELÉFONO en members; si es miembro activo paga precio de miembro.
  const checkGuestMembership = async (phoneRaw) => {
    const clean = (phoneRaw || '').replace(/[^\d]/g, '');
    if (clean.length < 7) { setGuestIsMember(null); setGuestLookup('idle'); return; }
    setGuestChecking(true);
    try {
      const dbRef = getFirestore();
      let found = null;
      const tries = [...new Set([phoneRaw.trim(), clean])];
      // eslint-disable-next-line no-restricted-syntax
      for (const p of tries) {
        // eslint-disable-next-line no-await-in-loop
        const snap = await getDocs(query(collection(dbRef, 'members'), where('phone', '==', p)));
        if (!snap.empty) { found = snap.docs[0].data(); break; }
      }
      setGuestIsMember(found ? found.active === true : false);
      setGuestLookup(found ? 'found' : 'not_found');
      setGuestEditing(false);
      if (found) {
        if (found.fullName) setGuestName(found.fullName);
        // Su email en sistema — para que siempre le llegue su confirmación
        if (found.email) setGuestEmail(found.email);
        const lic = found.licenseNumber || found.gunLicense;
        if (lic) setGuestLicense(lic);
      }
    } catch (e) {
      console.error(e);
      setGuestIsMember(false);
      setGuestLookup('not_found');
    }
    setGuestChecking(false);
  };

  // --- Acompañante en cursos (segunda persona, cobro doble) ---
  const [withCompanion, setWithCompanion] = useState(false);
  const [companionName, setCompanionName] = useState('');
  const [companionPhone, setCompanionPhone] = useState('');
  const [companionEmail, setCompanionEmail] = useState('');
  const [companionLicense, setCompanionLicense] = useState('');
  const [companionIsMember, setCompanionIsMember] = useState(null);
  const [companionChecking, setCompanionChecking] = useState(false);
  const [companionLookup, setCompanionLookup] = useState('idle'); // idle | found | not_found
  const [companionEditing, setCompanionEditing] = useState(false);
  // En lanes: ¿el acompañante tira en el mismo lane o en uno aparte?
  const [companionSeparateLane, setCompanionSeparateLane] = useState(false);

  const checkCompanionMembership = async (phoneRaw) => {
    const clean = (phoneRaw || '').replace(/[^\d]/g, '');
    if (clean.length < 7) { setCompanionIsMember(null); setCompanionLookup('idle'); return; }
    setCompanionChecking(true);
    try {
      const dbRef = getFirestore();
      let found = null;
      const tries = [...new Set([phoneRaw.trim(), clean])];
      // eslint-disable-next-line no-restricted-syntax
      for (const p of tries) {
        // eslint-disable-next-line no-await-in-loop
        const snap = await getDocs(query(collection(dbRef, 'members'), where('phone', '==', p)));
        if (!snap.empty) { found = snap.docs[0].data(); break; }
      }
      setCompanionIsMember(found ? found.active === true : false);
      setCompanionLookup(found ? 'found' : 'not_found');
      setCompanionEditing(false);
      if (found) {
        if (found.fullName) setCompanionName(found.fullName);
        // Su email en sistema — para que siempre le llegue su confirmación
        if (found.email) setCompanionEmail(found.email);
        const lic = found.licenseNumber || found.gunLicense;
        if (lic) setCompanionLicense(lic);
      }
    } catch (e) {
      console.error(e);
      setCompanionIsMember(false);
      setCompanionLookup('not_found');
    }
    setCompanionChecking(false);
  };

  // Al cambiar el teléfono se vuelve a empezar: los datos prellenados eran de
  // OTRA persona.
  const onCompanionPhoneChange = (raw) => {
    setCompanionPhone(phoneDigits(raw));
    setCompanionIsMember(null);
    if (companionLookup !== 'idle') {
      setCompanionLookup('idle');
      setCompanionEditing(false);
      setCompanionName('');
      setCompanionEmail('');
      setCompanionLicense('');
    }
  };
  const onGuestPhoneChange = (raw) => {
    setGuestPhone(phoneDigits(raw));
    setGuestIsMember(null);
    if (guestLookup !== 'idle') {
      setGuestLookup('idle');
      setGuestEditing(false);
      setGuestName('');
      setGuestEmail('');
      setGuestLicense('');
    }
  };

  const resetCompanion = () => {
    setWithCompanion(false);
    setCompanionName('');
    setCompanionPhone('');
    setCompanionEmail('');
    setCompanionLicense('');
    setCompanionIsMember(null);
    setCompanionLookup('idle');
    setCompanionEditing(false);
    setCompanionSeparateLane(false);
  };

  // "Reservar para otra persona" no aplica a cursos: si venía activado desde
  // otra categoría hay que limpiarlo, o se enviaría un reservationFor oculto.
  const resetForOther = () => {
    setForOther(false);
    setGuestName('');
    setGuestPhone('');
    setGuestEmail('');
    setGuestLicense('');
    setGuestIsMember(null);
    setGuestLookup('idle');
    setGuestEditing(false);
  };

  // --- Cupón de descuento (se valida contra la colección `coupons`) ---
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null); // cupón válido aplicado
  const [couponError, setCouponError] = useState('');
  const [couponChecking, setCouponChecking] = useState(false);

  const resetCoupon = () => {
    setCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponChecking(true);
    setCouponError('');
    setCoupon(null);
    try {
      const snap = await getDocs(query(collection(db, 'coupons'), where('code', '==', code)));
      if (snap.empty) {
        setCouponError('Cupón no válido.');
      } else {
        const c = { id: snap.docs[0].id, ...snap.docs[0].data() };
        const today = new Date().toISOString().split('T')[0];
        if (c.active !== true) setCouponError('Este cupón no está activo.');
        else if (c.validFrom && today < c.validFrom) setCouponError('Este cupón aún no está vigente.');
        else if (c.validUntil && today > c.validUntil) setCouponError('Este cupón ya expiró.');
        else if (Array.isArray(c.categoryIds) && c.categoryIds.length > 0 && !c.categoryIds.includes(selectedCategory?.id)) {
          setCouponError('Este cupón no aplica a esta categoría.');
        } else if (c.maxUses && (c.usedCount || 0) >= c.maxUses) setCouponError('Este cupón alcanzó su límite de usos.');
        else setCoupon(c);
      }
    } catch (e) {
      console.error(e);
      setCouponError('No se pudo verificar el cupón. Intenta de nuevo.');
    }
    setCouponChecking(false);
  };

  const isCourseCategory = COURSE_TYPES.includes(selectedCategory?.type);
  // En lanes el acompañante comparte el mismo lane (no ocupa un cupo aparte);
  // en cursos ocupa su propio cupo. En ambos casos se le puede añadir.
  const isLaneCategory = selectedCategory?.type === 'lane';
  const allowsCompanion = isCourseCategory || isLaneCategory;
  // El calibre se pide en categorías de tiro (para el registro del range)
  const asksCaliber = isLaneCategory || selectedCategory?.type === 'no-license-shooting';
  const isPrivate = isPrivateCourse(selectedCategory);
  const finalCalibers = [...selectedCalibers, ...(otherCaliber.trim() ? [otherCaliber.trim()] : [])];

  const handleConfirmBooking = async () => {
    if (isPrivate && (!courseLanguage || !licenseType)) {
      setError('Indica el idioma del curso (Español/Inglés) y si es licencia nueva o renovación.');
      return;
    }
    if (asksCaliber && finalCalibers.length === 0) {
      setError('Selecciona el calibre (o calibres) que vas a utilizar el día de tu reserva.');
      return;
    }
    if (withCompanion && allowsCompanion) {
      if (!companionPhone.trim()) {
        setError('Ingresa el teléfono de la otra persona.');
        return;
      }
      if (companionLookup === 'idle') {
        await checkCompanionMembership(companionPhone);
        setError('Buscamos a la otra persona por su teléfono. Revisa sus datos y confirma de nuevo.');
        return;
      }
      if (!companionName.trim()) {
        setError('Ingresa el nombre completo de la otra persona.');
        return;
      }
      // El acompañante no puede ser el mismo cliente (ponían su propio
      // teléfono otra vez y eso dañaba el registro / cobraba doble).
      if (phoneDigits(companionPhone) && phoneDigits(companionPhone) === phoneDigits(customerPhone)) {
        setError('El teléfono de la otra persona es el mismo tuyo. El acompañante debe ser OTRA persona: escribe su nombre y su teléfono.');
        return;
      }
      if (companionEmail.trim() && userEmail && companionEmail.trim().toLowerCase() === userEmail.trim().toLowerCase()) {
        setError('El email de la otra persona es el mismo tuyo. El acompañante debe ser OTRA persona.');
        return;
      }
      if (selectedCategory?.requiresLicense && !companionLicense.trim()) {
        setError('Esta categoría requiere el número de licencia de armas de la otra persona.');
        return;
      }
    }
    if (forOther) {
      if (!guestPhone.trim()) {
        setError('Ingresa el teléfono de la persona para quien es la reservación.');
        return;
      }
      if (guestLookup === 'idle') {
        await checkGuestMembership(guestPhone);
        setError('Buscamos a la persona por su teléfono. Revisa sus datos y confirma de nuevo.');
        return;
      }
      if (!guestName.trim()) {
        setError('Ingresa el nombre de la persona para quien es la reservación.');
        return;
      }
      if (phoneDigits(guestPhone) && phoneDigits(guestPhone) === phoneDigits(customerPhone)) {
        setError('El teléfono del invitado es el mismo tuyo. Si la reservación es para ti, desmarca "reservar para otra persona".');
        return;
      }
      if (selectedCategory?.requiresLicense && !guestLicense.trim()) {
        setError('Esta categoría requiere el número de licencia de armas del invitado.');
        return;
      }
    }
    if (chargeAmount > 0 && !selectedPM && !onceCardComplete) {
      setError('No has puesto o seleccionado una tarjeta.');
      return;
    }
    if (!customerName.trim()) {
      setError('Por favor ingresa tu nombre.');
      return;
    }
    if (!customerPhone.trim()) {
      setError('Por favor ingresa tu teléfono.');
      return;
    }
    if (userEmail.trim() && !isValidEmail(userEmail)) {
      setError('El email no parece válido. Corrígelo o déjalo en blanco.');
      setInfoExpanded(true);
      return;
    }
    if (selectedCategory?.requiresLicense && !customerLicense.trim()) {
      setError('Esta categoría requiere número de licencia de armas.');
      return;
    }
    setProcessing(true);
    setError('');
    try {
      // Si el cliente es nuevo (no estaba en members), lo creamos antes de la
      // reservación — pero ANTES se busca por email Y teléfono para no crear
      // duplicados (bug reportado: el mismo cliente salía 4-5 veces).
      if (!userData) {
        try {
          const dbRef = getFirestore();
          const cleanEmail = userEmail.toLowerCase().trim();
          const cleanPhone = phoneDigits(customerPhone);
          const incoming = { email: cleanEmail, fullName: customerName.trim() };
          let existingRef = null;
          if (cleanEmail) {
            const s = await getDocs(query(collection(dbRef, 'members'), where('email', '==', cleanEmail)));
            const best = pickBestMember(s.docs, { email: cleanEmail, phone: cleanPhone });
            if (best) existingRef = best._ref;
          }
          if (!existingRef && cleanPhone.length >= 7) {
            // eslint-disable-next-line no-restricted-syntax
            for (const p of [...new Set([cleanPhone, customerPhone.trim()])]) {
              // eslint-disable-next-line no-await-in-loop
              const s = await getDocs(query(collection(dbRef, 'members'), where('phone', '==', p)));
              const best = pickBestMember(s.docs, { email: cleanEmail, phone: cleanPhone });
              // Dos personas pueden compartir teléfono (matrimonios, papá e
              // hijo). Si el email o el nombre dicen que es OTRA persona, se
              // crea un récord aparte en vez de pisarle sus datos.
              if (best && isSamePerson(best, incoming)) { existingRef = best._ref; break; }
            }
          }
          const memberData = {
            email: cleanEmail,
            // Sin acentos: así el staff siempre lo encuentra en el dashboard
            fullName: stripAccents(customerName.trim()),
            phone: cleanPhone,
            licenseNumber: customerLicense.trim() || '',
            updatedAt: serverTimestamp(),
          };
          if (existingRef) {
            await setDoc(existingRef, memberData, { merge: true });
          } else {
            await setDoc(doc(collection(dbRef, 'members')), {
              ...memberData,
              active: false,
              createdAt: serverTimestamp(),
            });
          }
        } catch (e) {
          console.error('Error creando miembro:', e);
        }
      } else {
        // Si está en sistema pero algunos datos cambiaron, los actualizamos
        try {
          const dbRef = getFirestore();
          if (userData.id) {
            await setDoc(doc(dbRef, 'members', userData.id), {
              // Sin acentos: así el staff siempre lo encuentra en el dashboard
              fullName: stripAccents(customerName.trim()),
              phone: phoneDigits(customerPhone),
              licenseNumber: customerLicense.trim() || userData.licenseNumber || '',
              updatedAt: serverTimestamp(),
            }, { merge: true });
          }
        } catch (e) {
          console.error('Error actualizando miembro:', e);
        }
      }

      const res = await axios.post(`${FUNCTIONS_BASE}/createReservation`, {
        categoryId: selectedCategory.id,
        date: selectedDate,
        startTime: selectedSlot.startTime,
        email: userEmail.toLowerCase().trim() || null,
        // Identificador alterno: cuentas sin email (login por teléfono). Sin
        // esto el backend rechazaba la reservación con 400 y el pago nunca
        // se procesaba.
        phone: phoneDigits(customerPhone),
        // Tarjeta nueva completa = se usa esa (sin PM, el backend crea el
        // intent y aquí se confirma con el card element). El toggle "No
        // guardar" decide si queda guardada (saveCard).
        paymentMethodId: (!selectedPM && onceCardComplete) ? null : selectedPM,
        saveCard: (!selectedPM && onceCardComplete) ? !dontSaveCard : undefined,
        customerName: customerName.trim(),
        customerPhone: phoneDigits(customerPhone),
        licenseNumber: customerLicense.trim(),
        // Reservación para otra persona: el precio depende de si el invitado es miembro
        reservationFor: forOther ? {
          name: guestName.trim(),
          phone: guestPhone.trim(),
          email: guestEmail.trim().toLowerCase() || null,
          licenseNumber: guestLicense.trim() || null,
          isMember: guestIsMember === true,
        } : null,
        // Acompañante: en cursos ocupa su cupo (cobro doble); en lanes comparte
        // el lane y solo se cobra si no es miembro.
        guest: (withCompanion && allowsCompanion) ? {
          name: companionName.trim(),
          phone: companionPhone.trim(),
          email: companionEmail.trim().toLowerCase() || null,
          licenseNumber: companionLicense.trim() || null,
          isMember: companionIsMember === true,
          // true = ocupa su propio lane (se reservan dos lanes)
          separateLane: isLaneCategory && companionSeparateLane,
        } : null,
        // Lane escogido por el cliente ('' = que el sistema asigne uno al azar)
        laneId: selectedLaneId || null,
        // Cupón aplicado (el backend lo re-valida y calcula el descuento)
        couponCode: coupon ? coupon.code : null,
        // Calibres que usará (registro del campo de tiro)
        calibers: finalCalibers,
        // Curso privado: idioma y nueva/renovación
        courseLanguage: isPrivate ? courseLanguage : null,
        licenseType: isPrivate ? licenseType : null,
      });

      if (res.data.status === 'confirmed' || res.data.status === 'pending_payment') {
        // Tarjeta nueva: el intent viene sin método de pago y se confirma
        // aquí con el card element (Stripe maneja 3DS si aplica).
        if (!selectedPM && onceCardComplete && chargeAmount > 0 && res.data.clientSecret) {
          const stripeForOnce = stripeInstance || (window.Stripe ? window.Stripe(STRIPE_PK) : null);
          if (!stripeForOnce || !onceCard) {
            setError('No se pudo procesar la tarjeta. Recarga la página e intenta de nuevo.');
            setProcessing(false);
            return;
          }
          const onceResult = await stripeForOnce.confirmCardPayment(res.data.clientSecret, {
            // Con campos separados basta pasar el de número: Stripe toma la
            // fecha y el CVC de los elements hermanos.
            payment_method: { card: onceCard.number },
          });
          if (onceResult.error) {
            setError(onceResult.error.message || 'El pago no se pudo procesar. Verifica la tarjeta.');
            setProcessing(false);
            return;
          }
        } else if (res.data.requiresAction && res.data.clientSecret) {
          const stripeForAuth = stripeInstance || (window.Stripe ? window.Stripe(STRIPE_PK) : null);
          if (!stripeForAuth) {
            setError('No se pudo abrir la verificación del banco. Recarga la página e intenta de nuevo.');
            setProcessing(false);
            return;
          }
          const authResult = await stripeForAuth.confirmCardPayment(res.data.clientSecret);
          if (authResult.error) {
            setError(authResult.error.message || 'No se pudo completar la verificación con tu banco.');
            setProcessing(false);
            return;
          }
        }
        if (res.data.status === 'pending_payment' && res.data.paymentIntentId) {
          // Tras el 3DS el webhook de Stripe puede confirmar la reservación
          // antes que esta llamada (2026-10-17: clienta de Tiro Sin Licencia vio
          // "no se pudo confirmar" aunque ya estaba confirmada y reintentó 3
          // veces). "Ya confirmada" cuenta como éxito.
          let confirmStatus = null;
          try {
            const confirmRes = await axios.post(`${FUNCTIONS_BASE}/confirmReservationPayment`, {
              reservationId: res.data.reservationId,
              paymentIntentId: res.data.paymentIntentId,
            });
            confirmStatus = confirmRes.data.status;
          } catch (confirmErr) {
            const msg = String(confirmErr.response?.data?.error || '');
            if (/already confirmed/i.test(msg)) confirmStatus = 'confirmed';
            else throw confirmErr;
          }
          if (confirmStatus !== 'confirmed') {
            setError('El pago no se pudo confirmar. Intenta de nuevo.');
            setProcessing(false);
            return;
          }
        }
        // Persist latest customer info to localStorage for next visit
        const savedUser = localStorage.getItem('reservation_user');
        if (savedUser) {
          try {
            const parsed = JSON.parse(savedUser);
            parsed.fullName = customerName.trim();
            parsed.phone = customerPhone.trim();
            if (customerLicense.trim()) parsed.licenseNumber = customerLicense.trim();
            localStorage.setItem('reservation_user', JSON.stringify(parsed));
          } catch (e) { /* ignore */ }
        }

        setBookingResult(res.data);
        setActiveStep(5);
      } else {
        setError(res.data.error || 'Error al crear la reservación');
      }
    } catch (err) {
      const serverError = err.response?.data;
      const detail = serverError?.details || serverError?.error || 'Error al procesar la reservación';
      // Alguien tomó ese lane mientras el cliente confirmaba: lo devolvemos al
      // paso de Horario con asignación automática y los horarios recargados.
      if (serverError?.code === 'REQUESTED_LANE_UNAVAILABLE') {
        setSelectedLaneId('');
        setSelectedSlot(null);
        if (selectedCategory && selectedDate) {
          fetchSlots(selectedCategory.id, selectedDate);
        }
        setActiveStep(3);
      }
      setError(detail);
    }
    setProcessing(false);
  };

  const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;

  const formatTime = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h, 10);
    return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(`${dateStr}T12:00:00`);
    return d.toLocaleDateString('es-PR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const todayStr = new Date().toISOString().split('T')[0];
  // Si la reservación es para otra persona, el precio se basa en SU membresía
  const effectiveIsMember = forOther ? guestIsMember === true : isMember;
  const basePrice = selectedCategory ? (effectiveIsMember ? selectedCategory.priceMemberCents : selectedCategory.priceNonMemberCents) : 0;
  // Acompañante — mismo cálculo que el backend, usando el MISMO valor que se
  // envía (`companionIsMember === true`), para que lo mostrado sea lo cobrado:
  //   - no miembro (o sin verificar) → precio de no-miembro
  //   - miembro → en curso paga precio de miembro; en lane no paga (comparte lane)
  const companionCounts = withCompanion && allowsCompanion && selectedCategory;
  let companionExtra = 0;
  if (companionCounts) {
    if (companionIsMember === true) {
      companionExtra = isLaneCategory ? 0 : selectedCategory.priceMemberCents;
    } else {
      companionExtra = selectedCategory.priceNonMemberCents;
    }
  }
  const price = basePrice + companionExtra;
  // Descuento del cupón — mismo cálculo que el backend, sobre el precio total
  let couponDiscount = 0;
  if (coupon && price > 0) {
    couponDiscount = coupon.discountType === 'percent'
      ? Math.round((price * Math.min(Number(coupon.discountValue) || 0, 100)) / 100)
      : Math.min(Math.round(Number(coupon.discountValue) || 0), price);
  }
  const subtotalPrice = price - couponDiscount;
  // IVU 11.5% (10.5% estatal + 1% municipal) sobre el subtotal — mismo
  // cálculo que el backend (createReservation)
  const TAX_RATE = 0.115;
  const taxAmount = Math.round(Math.max(0, subtotalPrice) * TAX_RATE);
  const discountedPrice = subtotalPrice + taxAmount;
  const usesDeposit = selectedCategory?.depositEnabled === true && selectedCategory?.depositCents > 0 && discountedPrice > 0;
  // Depósito por cada persona QUE PAGA (mismo cálculo que el backend): quien
  // no se le cobra —por ser miembro— no genera depósito.
  const payingPeople = (basePrice > 0 ? 1 : 0) + (companionExtra > 0 ? 1 : 0);
  const depositAmount = usesDeposit ? Math.min(selectedCategory.depositCents * payingPeople, discountedPrice) : 0;
  const chargeAmount = usesDeposit ? depositAmount : discountedPrice;
  const remainingBalance = usesDeposit ? discountedPrice - depositAmount : 0;

  // Monta el card element (tarjeta nueva, siempre visible) al llegar al paso
  // de Confirmar con cobro > 0
  useEffect(() => {
    if (activeStep === 4 && chargeAmount > 0) mountOnceCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, chargeAmount]);

  // --- LOGIN SCREEN ---
  if (!isLoggedIn) {
    return (
      <PageWrapper>
        <Header>
          <Container maxWidth="sm">
            <Typography variant="h4" fontWeight="bold" textAlign="center">B & B Target Center</Typography>
            <Typography variant="body2" textAlign="center" sx={{ opacity: 0.7, mt: 0.5 }}>Sistema de Reservaciones</Typography>
          </Container>
        </Header>
        <Container maxWidth="sm">
          <Card sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom textAlign="center">Iniciar Sesión</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              Ingresa tu email o número de teléfono para reservar
            </Typography>
            {loginError && <Alert severity="error" sx={{ mb: 2 }}>{loginError}</Alert>}
            <TextField
              fullWidth
              label="Email o Teléfono"
              value={loginInput}
              onChange={(e) => setLoginInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              placeholder="ej. 7874380914 o tu@email.com"
              sx={{ mb: 2 }}
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleLogin}
              disabled={loginLoading}
              sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#b00100' }, py: 1.5 }}
            >
              {loginLoading ? <CircularProgress size={24} color="inherit" /> : 'Continuar'}
            </Button>
          </Card>
        </Container>
      </PageWrapper>
    );
  }

  // --- PIN VERIFICATION SCREEN ---
  if (!pinVerified) {
    return (
      <PageWrapper>
        <Header>
          <Container maxWidth="sm">
            <Typography variant="h4" fontWeight="bold" textAlign="center">B & B Target Center</Typography>
            <Typography variant="body2" textAlign="center" sx={{ opacity: 0.7, mt: 0.5 }}>Sistema de Reservaciones</Typography>
          </Container>
        </Header>
        <Container maxWidth="xs">
          <Card sx={{ p: 4, borderRadius: 3 }}>
            <LockIcon sx={{ fontSize: 48, color: '#d40100', display: 'block', mx: 'auto', mb: 1 }} />

            {pinStep === 'verify' && (
              <>
                <Typography variant="h5" fontWeight="bold" gutterBottom textAlign="center">Verificación</Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
                  Ingresa tu PIN de 4 dígitos para continuar
                </Typography>
                {pinError && <Alert severity="error" sx={{ mb: 2 }}>{pinError}</Alert>}
                <TextField
                  fullWidth
                  type="password"
                  value={pinValue}
                  onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  onKeyDown={(e) => e.key === 'Enter' && pinValue.length === 4 && handlePinVerify()}
                  inputProps={{ maxLength: 4, inputMode: 'numeric', style: { textAlign: 'center', fontSize: 32, letterSpacing: 16, fontWeight: 'bold' } }}
                  placeholder="••••"
                  autoFocus
                  sx={{ mb: 2 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handlePinVerify}
                  disabled={pinLoading || pinValue.length !== 4}
                  sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#b00100' }, py: 1.5, mb: 1 }}
                >
                  {pinLoading ? <CircularProgress size={24} color="inherit" /> : 'Verificar'}
                </Button>
                {pinResetSent ? (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Te enviamos un email con un link para restablecer tu PIN.
                    Revisa tu bandeja (y spam) — el link expira en 1 hora.
                  </Alert>
                ) : (
                  <Button
                    fullWidth
                    size="small"
                    onClick={handleForgotPin}
                    disabled={pinResetLoading}
                    sx={{ color: '#d40100', textTransform: 'none', fontWeight: 600 }}
                  >
                    {pinResetLoading ? 'Enviando email...' : '¿Olvidaste tu PIN?'}
                  </Button>
                )}
              </>
            )}

            {pinStep === 'create' && (
              <>
                <Typography variant="h5" fontWeight="bold" gutterBottom textAlign="center">Configura tu PIN</Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
                  Crea un PIN de 4 dígitos para proteger tu cuenta
                </Typography>
                {pinError && <Alert severity="error" sx={{ mb: 2 }}>{pinError}</Alert>}
                <TextField
                  fullWidth
                  type="password"
                  label="Crea tu PIN"
                  value={pinValue}
                  onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  inputProps={{ maxLength: 4, inputMode: 'numeric', style: { textAlign: 'center', fontSize: 32, letterSpacing: 16, fontWeight: 'bold' } }}
                  placeholder="••••"
                  autoFocus
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Confirma tu PIN"
                  value={pinConfirm}
                  onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  onKeyDown={(e) => e.key === 'Enter' && pinValue.length === 4 && pinConfirm.length === 4 && handlePinCreate()}
                  inputProps={{ maxLength: 4, inputMode: 'numeric', style: { textAlign: 'center', fontSize: 32, letterSpacing: 16, fontWeight: 'bold' } }}
                  placeholder="••••"
                  sx={{ mb: 2 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handlePinCreate}
                  disabled={pinLoading || pinValue.length !== 4 || pinConfirm.length !== 4}
                  sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#b00100' }, py: 1.5, mb: 1 }}
                >
                  {pinLoading ? <CircularProgress size={24} color="inherit" /> : 'Guardar PIN'}
                </Button>
              </>
            )}

            {!pinStep && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={32} sx={{ color: '#d40100' }} />
              </Box>
            )}

            <Button
              fullWidth
              size="small"
              onClick={handleLogout}
              sx={{ color: 'text.secondary', textTransform: 'none' }}
            >
              Cambiar cuenta
            </Button>
          </Card>
        </Container>
      </PageWrapper>
    );
  }

  // --- SUCCESS SCREEN ---
  if (bookingResult) {
    return (
      <PageWrapper>
        <Header>
          <Container maxWidth="sm">
            <Typography variant="h4" fontWeight="bold" textAlign="center">B & B Target Center</Typography>
          </Container>
        </Header>
        <Container maxWidth="sm">
          <Card sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: '#28a745', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>¡Reserva Confirmada!</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>Tu reservación ha sido procesada exitosamente</Typography>

            <Card sx={{ bgcolor: '#333', color: '#FFF', p: 3, borderRadius: 2, mb: 3 }}>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>Código de Confirmación</Typography>
              <Typography variant="h3" fontWeight="bold" sx={{ letterSpacing: 4, my: 1 }}>{bookingResult.confirmationCode}</Typography>
            </Card>

            <Stack spacing={1.5} sx={{ textAlign: 'left', mb: 3 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Categoría</Typography>
                <Typography variant="body2" fontWeight="600">{selectedCategory.name}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Fecha</Typography>
                <Typography variant="body2" fontWeight="600">{formatDateDisplay(selectedDate)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Hora</Typography>
                <Typography variant="body2" fontWeight="600">{formatTime(selectedSlot.startTime)} - {formatTime(selectedSlot.endTime)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">{usesDeposit ? 'Depósito pagado' : 'Pagado'}</Typography>
                <Typography variant="body2" fontWeight="600">{formatPrice(chargeAmount)}</Typography>
              </Stack>
              {usesDeposit && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Restante (pagar en persona)</Typography>
                  <Typography variant="body2" fontWeight="600" sx={{ color: '#E65100' }}>{formatPrice(remainingBalance)}</Typography>
                </Stack>
              )}
            </Stack>

            {usesDeposit && (
              <Box sx={{ bgcolor: '#FFF3E0', border: '2px solid #FF9800', borderRadius: 2, p: 2, mt: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <InfoOutlinedIcon sx={{ color: '#E65100', fontSize: 28, mt: 0.25 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#E65100', mb: 0.5 }}>
                    Balance pendiente: {formatPrice(remainingBalance)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#BF360C' }}>
                    {selectedCategory?.depositMessage || `El restante de ${formatPrice(remainingBalance)} se debe pagar en persona el día de la reservación.`}
                  </Typography>
                </Box>
              </Box>
            )}

            <Stack spacing={1.5}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                href="/my-reservations"
                sx={{ bgcolor: '#333', '&:hover': { bgcolor: '#555' } }}
              >
                Ver Mis Reservaciones
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => { setBookingResult(null); setActiveStep(0); setBookingType(null); setSelectedCategory(null); setSelectedDate(''); setSelectedSlot(null); }}
              >
                Hacer Otra Reservación
              </Button>
            </Stack>
          </Card>
        </Container>
      </PageWrapper>
    );
  }

  // --- MAIN BOOKING FLOW ---
  return (
    <PageWrapper>
      <Header>
        <Container maxWidth="md">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold">B & B Target Center</Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                {userData?.fullName || userEmail || customerPhone}
                {isMember && <Chip label="Miembro" size="small" sx={{ ml: 1, bgcolor: '#28a745', color: '#FFF', height: 20 }} />}
              </Typography>
              <Button size="small" variant="outlined" onClick={handleLogout} sx={{ color: '#FFF', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none' }}>
                Salir
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Header>

      <Container maxWidth="xl">
        {/* Stepper */}
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* STEP 0: Booking Type Selection */}
        {activeStep === 0 && (
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>¿Qué deseas hacer?</Typography>
              <Typography variant="body1" color="text.secondary">Selecciona una opción para continuar</Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box
                  onClick={() => handleSelectBookingType('shooting')}
                  sx={{
                    position: 'relative',
                    borderRadius: 4,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    height: 300,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 24px 48px rgba(0,0,0,0.3)',
                    },
                    '&:hover .type-overlay': {
                      background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0.2) 100%)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={defaultImages.lane}
                    alt="Campo de Tiro"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <Box
                    className="type-overlay"
                    sx={{
                      position: 'absolute', inset: 0,
                      background: 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.1) 100%)',
                      transition: 'background 0.3s ease',
                    }}
                  />
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 3.5, textAlign: 'center' }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#d40100', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                      <Typography sx={{ fontSize: 28 }}>🎯</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ color: '#FFF', fontWeight: 800, mb: 1 }}>
                      Campo de Tiro
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 15 }}>
                      Lanes de pistola, rifle y tiro sin licencia
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  onClick={() => handleSelectBookingType('courses')}
                  sx={{
                    position: 'relative',
                    borderRadius: 4,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    height: 300,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 24px 48px rgba(0,0,0,0.3)',
                    },
                    '&:hover .type-overlay': {
                      background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0.2) 100%)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={defaultImages.course}
                    alt="Cursos"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <Box
                    className="type-overlay"
                    sx={{
                      position: 'absolute', inset: 0,
                      background: 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.1) 100%)',
                      transition: 'background 0.3s ease',
                    }}
                  />
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 3.5, textAlign: 'center' }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#d40100', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                      <Typography sx={{ fontSize: 28 }}>📋</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ color: '#FFF', fontWeight: 800, mb: 1 }}>
                      Cursos
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 15 }}>
                      Licencia nueva, renovación y más
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* STEP 1: Categories */}
        {activeStep === 1 && (
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>¿Qué deseas reservar?</Typography>
              <Typography variant="body1" color="text.secondary">Selecciona una opción para continuar</Typography>
            </Box>

            {!loading && categories.length > 0 && (() => {
              const allowedTypes = bookingType === 'shooting' ? SHOOTING_TYPES : COURSE_TYPES;
              const filteredByType = categories.filter((c) => allowedTypes.includes(c.type));
              const subTypes = [...new Set(filteredByType.map((c) => c.type))];
              return subTypes.length > 1 ? (
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label="Todos"
                    variant={!categoryFilter ? 'filled' : 'outlined'}
                    onClick={() => setCategoryFilter('')}
                    sx={{
                      fontWeight: 600, fontSize: 13, px: 0.5,
                      ...(! categoryFilter ? { bgcolor: '#333', color: '#FFF', '&:hover': { bgcolor: '#444' } } : {}),
                    }}
                  />
                  {subTypes.map((t) => (
                    <Chip
                      key={t}
                      label={typeLabels[t] || t}
                      variant={categoryFilter === t ? 'filled' : 'outlined'}
                      onClick={() => setCategoryFilter(categoryFilter === t ? '' : t)}
                      sx={{
                        fontWeight: 600, fontSize: 13, px: 0.5,
                        ...(categoryFilter === t ? { bgcolor: '#d40100', color: '#FFF', '&:hover': { bgcolor: '#b00100' } } : {}),
                      }}
                    />
                  ))}
                </Stack>
              ) : null;
            })()}

            {loading ? (
              <Grid container spacing={2.5}>
                {[0, 1, 2, 3].map((i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Skeleton variant="rounded" height={200} animation="wave" sx={{ borderRadius: 4 }} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Grid container spacing={2.5}>
                {categories.filter((c) => {
                  const allowedTypes = bookingType === 'shooting' ? SHOOTING_TYPES : COURSE_TYPES;
                  if (!allowedTypes.includes(c.type)) return false;
                  if (categoryFilter && c.type !== categoryFilter) return false;
                  return true;
                }).map((cat) => {
                  const catPrice = isMember ? cat.priceMemberCents : cat.priceNonMemberCents;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={cat.id}>
                      <Box
                        onClick={() => handleSelectCategory(cat)}
                        sx={{
                          position: 'relative',
                          borderRadius: 4,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          height: 200,
                          transition: 'all 0.25s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
                          },
                          '&:hover .cat-overlay': {
                            background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.15) 100%)',
                          },
                          '&:hover .cat-arrow': {
                            opacity: 1,
                            transform: 'translateX(0)',
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={cat.imageUrl || defaultImages[cat.type] || defaultImages.lane}
                          alt={cat.name}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                        <Box
                          className="cat-overlay"
                          sx={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.05) 100%)',
                            transition: 'background 0.25s ease',
                          }}
                        />
                        <Chip
                          label={typeLabels[cat.type] || cat.type}
                          size="small"
                          sx={{
                            position: 'absolute', top: 14, left: 14,
                            height: 26, fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3,
                            bgcolor: 'rgba(255,255,255,0.92)', color: '#1a1a1a',
                            backdropFilter: 'blur(8px)',
                          }}
                        />
                        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2.5 }}>
                          <Typography variant="h6" sx={{ color: '#FFF', fontWeight: 800, lineHeight: 1.2, mb: 0.5, fontSize: 20 }}>
                            {cat.name}
                          </Typography>
                          {cat.description && (
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 1.3, mb: 1.5 }} noWrap>
                              {cat.description}
                            </Typography>
                          )}
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <AccessTimeIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} />
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 12.5, fontWeight: 500 }}>
                                  {cat.durationMinutes} min
                                </Typography>
                              </Stack>
                              <Typography variant="subtitle1" sx={{ color: '#FFF', fontWeight: 800, fontSize: 18 }}>
                                {formatPrice(catPrice)}
                              </Typography>
                              {isMember && cat.priceNonMemberCents > cat.priceMemberCents && (
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'line-through', fontSize: 13 }}>
                                  {formatPrice(cat.priceNonMemberCents)}
                                </Typography>
                              )}
                            </Stack>
                            <Box
                              className="cat-arrow"
                              sx={{
                                width: 36, height: 36, borderRadius: '50%',
                                bgcolor: '#d40100', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                opacity: 0, transform: 'translateX(8px)',
                                transition: 'all 0.25s ease',
                              }}
                            >
                              <ArrowForwardIcon sx={{ fontSize: 18, color: '#FFF' }} />
                            </Box>
                          </Stack>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
                {categories.filter((c) => {
                  const allowedTypes = bookingType === 'shooting' ? SHOOTING_TYPES : COURSE_TYPES;
                  if (!allowedTypes.includes(c.type)) return false;
                  if (categoryFilter && c.type !== categoryFilter) return false;
                  return true;
                }).length === 0 && !loading && (
                  <Grid item xs={12}>
                    <Box sx={{ py: 8, textAlign: 'center' }}>
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        {categoryFilter ? 'No hay opciones en esta categoría' : 'No hay categorías disponibles'}
                      </Typography>
                      <Typography variant="body2" color="text.disabled">
                        {categoryFilter ? 'Prueba con otro filtro' : 'Contacta al negocio para más información'}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        )}

        {/* STEP 2: Date */}
        {activeStep === 2 && selectedCategory && (
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <DateStepContent
              selectedCategory={selectedCategory}
              price={price}
              formatPrice={formatPrice}
              todayStr={todayStr}
              onSelectDate={handleSelectDate}
            />
          </Box>
        )}

        {/* STEP 3: Time Slots */}
        {activeStep === 3 && selectedCategory && selectedDate && (
          <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>Selecciona un Horario</Typography>
            <Card sx={{ p: 2, borderRadius: 3, mb: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <CalendarTodayIcon fontSize="small" color="action" />
                <Typography variant="body2">{formatDateDisplay(selectedDate)}</Typography>
              </Stack>
            </Card>

            {/* El lane se escoge ANTES de la hora: así los horarios que salen
                son los de ese lane y el cliente no escoge una hora en la que
                su lane no está libre. */}
            {isLaneCategory && (
              <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                  ¿Quieres escoger tu lane?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Si escoges uno, abajo verás solo los horarios en que ese lane está libre.
                  Si no, te asignamos uno disponible automáticamente.
                </Typography>
                {lanesLoading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Cargando lanes...</Typography>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      label="Cualquiera disponible"
                      onClick={() => handleSelectLane('')}
                      color={selectedLaneId === '' ? 'error' : 'default'}
                      variant={selectedLaneId === '' ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 600 }}
                    />
                    {availableLanes.map((lane) => (
                      <Chip
                        key={lane.id}
                        label={lane.name}
                        onClick={() => handleSelectLane(lane.id)}
                        color={selectedLaneId === lane.id ? 'error' : 'default'}
                        variant={selectedLaneId === lane.id ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 600 }}
                      />
                    ))}
                  </Stack>
                )}
              </Card>
            )}

            {loading ? (
              <Grid container spacing={2}>
                {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <Grid item xs={6} sm={4} md={3} key={i}>
                    <Card sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                      <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} height={26} animation="wave" />
                      <Skeleton variant="text" width="40%" sx={{ mx: 'auto' }} height={18} animation="wave" />
                      <Skeleton variant="text" width="50%" sx={{ mx: 'auto', mt: 0.5 }} height={16} animation="wave" />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Grid container spacing={2}>
                {/* Los horarios llenos no se muestran (pedido del cliente) */}
                {slots.filter((slot) => slot.available).map((slot, i) => (
                  <Grid item xs={6} sm={4} md={3} key={i}>
                    <Card
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: selectedSlot?.startTime === slot.startTime ? '2px solid #d40100' : '2px solid transparent',
                        bgcolor: selectedSlot?.startTime === slot.startTime ? '#FFF5F5' : '#FFF',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: '#d40100', transform: 'translateY(-2px)' },
                      }}
                      onClick={() => handleSelectSlot(slot)}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">{formatTime(slot.startTime)}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatTime(slot.endTime)}</Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#28a745' }}>
                        {selectedLaneId
                          ? 'Lane disponible'
                          : `${slot.remainingSpots} disponible${slot.remainingSpots > 1 ? 's' : ''}`}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
                {slots.filter((slot) => slot.available).length === 0 && !loading && (
                  <Grid item xs={12}>
                    <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                      {selectedLaneId
                        ? 'Ese lane no tiene horarios libres esta fecha. Escoge otro lane o "Cualquiera disponible".'
                        : 'No hay horarios disponibles para esta fecha'}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        )}

        {/* STEP 4: Confirm & Pay */}
        {activeStep === 4 && selectedCategory && selectedDate && selectedSlot && (
          <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>Confirmar Reservación</Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                {/* Summary */}
                <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Resumen</Typography>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Categoría</Typography>
                      <Typography variant="body2" fontWeight="600">{selectedCategory.name}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Fecha</Typography>
                      <Typography variant="body2" fontWeight="600">{formatDateDisplay(selectedDate)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Hora</Typography>
                      <Typography variant="body2" fontWeight="600">{formatTime(selectedSlot.startTime)} - {formatTime(selectedSlot.endTime)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Duración</Typography>
                      <Typography variant="body2" fontWeight="600">{selectedCategory.durationMinutes} minutos</Typography>
                    </Stack>
                  </Stack>
                </Card>

                {/* Curso privado: idioma y nueva/renovación (obligatorio) */}
                {isPrivate && (
                  <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Detalles del curso privado
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      ¿En qué idioma se dará el curso?
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {COURSE_LANGUAGE_OPTIONS.map((o) => (
                        <Chip
                          key={o.value}
                          label={o.label}
                          clickable
                          onClick={() => setCourseLanguage(o.value)}
                          color={courseLanguage === o.value ? 'error' : 'default'}
                          variant={courseLanguage === o.value ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 600 }}
                        />
                      ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      ¿Es para licencia nueva o renovación?
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {LICENSE_TYPE_OPTIONS.map((o) => (
                        <Chip
                          key={o.value}
                          label={o.label}
                          clickable
                          onClick={() => setLicenseType(o.value)}
                          color={licenseType === o.value ? 'error' : 'default'}
                          variant={licenseType === o.value ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 600 }}
                        />
                      ))}
                    </Box>
                  </Card>
                )}

                {/* Calibre(s) que va a utilizar — registro del campo de tiro */}
                {asksCaliber && (
                  <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      ¿Qué calibre vas a utilizar?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Selecciona todos los que apliquen — es parte del registro del campo de tiro.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {CALIBER_OPTIONS.map((cal) => (
                        <Chip
                          key={cal}
                          label={cal}
                          clickable
                          onClick={() => toggleCaliber(cal)}
                          color={selectedCalibers.includes(cal) ? 'error' : 'default'}
                          variant={selectedCalibers.includes(cal) ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 600 }}
                        />
                      ))}
                    </Box>
                    <TextField
                      label="Otro calibre"
                      value={otherCaliber}
                      onChange={(e) => setOtherCaliber(e.target.value)}
                      size="small"
                      sx={{ mt: 2 }}
                      placeholder="Ej: 10MM"
                    />
                  </Card>
                )}

                {/* Tu información */}
                <Card sx={{ borderRadius: 3, mb: 3, overflow: 'hidden' }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={() => setInfoExpanded(!infoExpanded)}
                    sx={{ p: 3, pb: infoExpanded ? 0 : 3, cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Typography variant="subtitle1" fontWeight="bold">Tu información</Typography>
                      {userData ? (
                        <Chip
                          label="Encontrado en sistema"
                          size="small"
                          sx={{ bgcolor: '#E8F5E9', color: '#28a745', fontWeight: 600 }}
                        />
                      ) : (
                        <Chip
                          label="Nuevo cliente"
                          size="small"
                          sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600 }}
                        />
                      )}
                    </Stack>
                    <IconButton size="small" sx={{ transition: 'transform 0.2s', transform: infoExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      <KeyboardArrowDownIcon />
                    </IconButton>
                  </Stack>

                  {!infoExpanded && customerName && (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 2, mt: -1 }}>
                      {customerName}{customerPhone ? ` • ${customerPhone}` : ''}
                    </Typography>
                  )}

                  <Collapse in={infoExpanded}>
                    <Box sx={{ p: 3, pt: 2 }}>
                      {userData && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          Reconocimos tu cuenta con email <strong>{userEmail}</strong>. Verifica que la información sea correcta.
                        </Alert>
                      )}
                      {!userData && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          No encontramos tu cuenta. Completa tu información para continuar; te crearemos una.
                        </Alert>
                      )}

                      <Stack spacing={2}>
                        <TextField
                          label="Nombre completo"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          fullWidth
                          required
                          size="small"
                        />
                        {/* El email solo se bloquea cuando viene de una cuenta
                            del sistema. Antes estaba SIEMPRE disabled y quien
                            entraba con su teléfono (cliente nuevo) no podía
                            escribirlo — la reservación fallaba sin email. */}
                        <TextField
                          label={userData?.email ? 'Email' : 'Email (opcional)'}
                          type="email"
                          value={userEmail}
                          onChange={(e) => setUserEmail(e.target.value)}
                          disabled={!!userData?.email}
                          fullWidth
                          size="small"
                          placeholder="tucorreo@ejemplo.com"
                          error={!userData?.email && !!userEmail.trim() && !isValidEmail(userEmail)}
                          helperText={
                            userData?.email
                              ? 'Para cambiar el email, sal e inicia sesión con otro.'
                              : (!!userEmail.trim() && !isValidEmail(userEmail))
                                ? 'Ese email no parece válido.'
                                : 'Aquí te enviamos la confirmación. Si no tienes email, déjalo en blanco.'
                          }
                        />
                        <TextField
                          label="Teléfono"
                          value={formatPhoneDisplay(customerPhone)}
                          onChange={(e) => setCustomerPhone(phoneDigits(e.target.value))}
                          fullWidth
                          required
                          size="small"
                          placeholder="787-555-1234"
                        />
                        {selectedCategory?.requiresLicense && (
                          <Alert severity="warning" icon={false} sx={{ fontWeight: 600, border: '2px solid #FF9800' }}>
                            ⚠️ Esta categoría requiere número de licencia de armas para reservar.
                          </Alert>
                        )}
                        <TextField
                          label={selectedCategory?.requiresLicense ? 'Número de licencia (requerido)' : 'Número de licencia (opcional)'}
                          value={customerLicense}
                          onChange={(e) => setCustomerLicense(e.target.value)}
                          fullWidth
                          size="small"
                          required={!!selectedCategory?.requiresLicense}
                          error={selectedCategory?.requiresLicense && !customerLicense.trim()}
                          helperText={selectedCategory?.requiresLicense && !customerLicense.trim() ? 'Debes ingresar tu número de licencia para continuar' : ''}
                        />
                        <Button
                          size="small"
                          startIcon={<LockIcon />}
                          onClick={() => setChangePinOpen(true)}
                          sx={{ alignSelf: 'flex-start', textTransform: 'none', color: '#d40100', fontWeight: 600 }}
                        >
                          Cambiar mi PIN
                        </Button>
                      </Stack>

                      {/* Dialog cambiar PIN */}
                      <Dialog open={changePinOpen} onClose={() => !changePinSaving && setChangePinOpen(false)} maxWidth="xs" fullWidth>
                        <DialogTitle>Cambiar mi PIN</DialogTitle>
                        <DialogContent dividers>
                          {changePinDone ? (
                            <Alert severity="success">¡PIN actualizado! Úsalo la próxima vez que entres.</Alert>
                          ) : (
                            <Stack spacing={2} sx={{ pt: 1 }}>
                              {changePinError && <Alert severity="error">{changePinError}</Alert>}
                              <TextField
                                label="Nuevo PIN"
                                type="password"
                                value={newPin}
                                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                inputProps={{ maxLength: 4, inputMode: 'numeric', style: { textAlign: 'center', letterSpacing: 8, fontSize: 20 } }}
                                fullWidth
                              />
                              <TextField
                                label="Confirma tu nuevo PIN"
                                type="password"
                                value={newPinConfirm}
                                onChange={(e) => setNewPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                inputProps={{ maxLength: 4, inputMode: 'numeric', style: { textAlign: 'center', letterSpacing: 8, fontSize: 20 } }}
                                fullWidth
                              />
                            </Stack>
                          )}
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={() => setChangePinOpen(false)} disabled={changePinSaving}>Cerrar</Button>
                          {!changePinDone && (
                            <Button
                              variant="contained"
                              onClick={handleChangePin}
                              disabled={changePinSaving || newPin.length !== 4 || newPinConfirm.length !== 4}
                            >
                              {changePinSaving ? 'Guardando...' : 'Guardar PIN'}
                            </Button>
                          )}
                        </DialogActions>
                      </Dialog>
                    </Box>
                  </Collapse>
                </Card>

                {/* El lane ya se escogió en el paso de Horario; aquí solo se
                    confirma. Si se piden dos lanes, los asigna el sistema. */}
                {isLaneCategory && selectedLaneId && !(withCompanion && companionSeparateLane) && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Lane escogido: <b>{availableLanes.find((l) => l.id === selectedLaneId)?.name || '—'}</b>.
                    Puedes cambiarlo volviendo al paso de Horario.
                  </Alert>
                )}

                {/* ¿Otra persona? (acompañante) — prominente */}
                {allowsCompanion && (
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      mb: 3,
                      border: withCompanion ? '2px solid #d40100' : '2px dashed #d40100',
                      bgcolor: withCompanion ? '#FFF5F5' : '#FFF',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={withCompanion}
                          onChange={(e) => setWithCompanion(e.target.checked)}
                          color="error"
                        />
                      }
                      label={
                        <Typography variant="subtitle1" fontWeight="bold">
                          {isLaneCategory
                            ? '¿Deseas separar un lane para otra persona?'
                            : '¿Quieres añadir otra persona al curso?'}
                        </Typography>
                      }
                    />
                    <Collapse in={withCompanion}>
                      <Stack spacing={2} sx={{ mt: 2 }}>
                        <Alert severity="info">
                          {isLaneCategory
                            ? 'Si es miembro activo no se cobra; si no es miembro, se le cobra su tarifa.'
                            : 'La otra persona ocupa su propio cupo en el curso y se cobra su precio también.'}
                        </Alert>

                        {/* ¿Mismo lane o dos lanes? */}
                        {isLaneCategory && (
                          <Box sx={{ border: '1px solid #eee', borderRadius: 2, p: 1.5 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                              ¿Dónde va a tirar la otra persona?
                            </Typography>
                            <RadioGroup
                              value={companionSeparateLane ? 'separate' : 'same'}
                              onChange={(e) => {
                                setCompanionSeparateLane(e.target.value === 'separate');
                                // Con dos lanes el sistema los asigna juntos;
                                // escoger uno a mano no aplica.
                                if (e.target.value === 'separate') setSelectedLaneId('');
                              }}
                            >
                              <FormControlLabel
                                value="same"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">En el mismo lane que yo</Typography>}
                              />
                              <FormControlLabel
                                value="separate"
                                control={<Radio size="small" />}
                                label={<Typography variant="body2">En su propio lane (se reservan 2 lanes)</Typography>}
                              />
                            </RadioGroup>
                            {companionSeparateLane && (
                              <Alert severity="info" sx={{ mt: 1 }}>
                                Les asignamos dos lanes disponibles, uno al lado del otro cuando se pueda.
                              </Alert>
                            )}
                          </Box>
                        )}
                        {/* 1) Teléfono primero → buscamos a la persona */}
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <TextField
                            label="Teléfono de la otra persona"
                            value={formatPhoneDisplay(companionPhone)}
                            onChange={(e) => onCompanionPhoneChange(e.target.value)}
                            onBlur={() => { if (companionLookup === 'idle') checkCompanionMembership(companionPhone); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); checkCompanionMembership(companionPhone); } }}
                            fullWidth
                            required
                            size="small"
                            placeholder="787-555-1234"
                            helperText={companionLookup === 'idle' ? 'Con el teléfono buscamos si ya está en el sistema' : ''}
                            InputProps={{
                              endAdornment: companionChecking ? <CircularProgress size={16} /> : (
                                companionIsMember === true
                                  ? <Chip label="Miembro ✓" size="small" sx={{ bgcolor: '#E8F5E9', color: '#28a745', fontWeight: 600 }} />
                                  : companionIsMember === false
                                    ? <Chip label="No miembro" size="small" sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600 }} />
                                    : null
                              ),
                            }}
                          />
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => checkCompanionMembership(companionPhone)}
                            disabled={companionChecking || phoneDigits(companionPhone).length < 7}
                            sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
                          >
                            Buscar
                          </Button>
                        </Stack>

                        {/* 2a) Existe → resumen con sus datos */}
                        {companionLookup === 'found' && !companionEditing && (
                          <Box sx={{ border: '1px solid #C8E6C9', bgcolor: '#F1F8E9', borderRadius: 2, p: 1.5 }}>
                            <Typography variant="body2" fontWeight={700}>{companionName}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {formatPhoneDisplay(companionPhone)}{companionEmail ? ` · ${companionEmail}` : ''}
                              {companionLicense ? ` · Lic. ${companionLicense}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Ya está en el sistema — no tienes que llenar sus datos.{' '}
                              <Box component="span" onClick={() => setCompanionEditing(true)} sx={{ color: '#d40100', cursor: 'pointer', textDecoration: 'underline' }}>
                                Corregir datos
                              </Box>
                            </Typography>
                            {selectedCategory?.requiresLicense && !companionLicense.trim() && (
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                No tiene licencia de armas registrada — escríbela con "Corregir datos".
                              </Alert>
                            )}
                          </Box>
                        )}

                        {/* 2b) No existe (o quieren corregir) → llenar datos */}
                        {(companionLookup === 'not_found' || companionEditing) && (
                          <>
                            {companionLookup === 'not_found' && (
                              <Typography variant="caption" color="text.secondary">
                                No está en el sistema — llena sus datos:
                              </Typography>
                            )}
                            <TextField
                              label="Nombre completo"
                              value={companionName}
                              onChange={(e) => setCompanionName(e.target.value)}
                              fullWidth
                              required
                              size="small"
                            />
                            <TextField
                              label="Email (para enviarle su confirmación)"
                              value={companionEmail}
                              onChange={(e) => setCompanionEmail(e.target.value)}
                              fullWidth
                              size="small"
                              type="email"
                              placeholder="opcional"
                              helperText="Si lo pones, la otra persona también recibe el email de confirmación"
                            />
                            <TextField
                              label={selectedCategory?.requiresLicense ? 'Número de licencia' : 'Número de licencia (opcional)'}
                              value={companionLicense}
                              onChange={(e) => setCompanionLicense(e.target.value)}
                              fullWidth
                              required={selectedCategory?.requiresLicense === true}
                              size="small"
                            />
                          </>
                        )}
                        {withCompanion && (
                          <Alert severity={companionExtra > 0 ? 'warning' : 'success'}>
                            {companionExtra > 0 ? (
                              <>
                                Total por 2 personas: <b>{formatPrice(price)}</b>
                                {usesDeposit ? ` — hoy se cobra el depósito de ${formatPrice(depositAmount)} y el resto en el local.` : '.'}
                              </>
                            ) : (
                              <>La otra persona es miembro activo — no se cobra extra. Total: <b>{formatPrice(price)}</b>.</>
                            )}
                          </Alert>
                        )}
                      </Stack>
                    </Collapse>
                  </Card>
                )}

                {/* ¿Reservación para otra persona? — en TODAS las categorías
                    (pedido 2026-09-30: Coral le reserva al hijo y la reservación
                    tiene que quedar a nombre del hijo, no de ella). Es distinto
                    del acompañante: aquí quien reserva NO va a tirar. */}
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    mb: 3,
                    border: forOther ? '2px solid #d40100' : '2px dashed #d40100',
                    bgcolor: forOther ? '#FFF5F5' : '#FFF',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={forOther}
                        onChange={(e) => setForOther(e.target.checked)}
                        color="error"
                      />
                    }
                    label={
                      <Typography variant="subtitle1" fontWeight="bold">
                        La reservación es para otra persona (yo no voy a tirar)
                      </Typography>
                    }
                  />
                  <Collapse in={forOther}>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                      <Alert severity="info">
                        La reservación quedará a nombre de esta persona. Si es
                        miembro activo paga precio de miembro; si no, precio regular.
                      </Alert>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField
                          label="Teléfono del invitado"
                          value={formatPhoneDisplay(guestPhone)}
                          onChange={(e) => onGuestPhoneChange(e.target.value)}
                          onBlur={() => { if (guestLookup === 'idle') checkGuestMembership(guestPhone); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); checkGuestMembership(guestPhone); } }}
                          fullWidth
                          required
                          size="small"
                          placeholder="787-555-1234"
                          helperText={guestLookup === 'idle' ? 'Con el teléfono buscamos si ya está en el sistema' : ''}
                          InputProps={{
                            endAdornment: guestChecking ? <CircularProgress size={16} /> : (
                              guestIsMember === true
                                ? <Chip label="Miembro ✓" size="small" sx={{ bgcolor: '#E8F5E9', color: '#28a745', fontWeight: 600 }} />
                                : guestIsMember === false
                                  ? <Chip label="No miembro" size="small" sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600 }} />
                                  : null
                            ),
                          }}
                        />
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => checkGuestMembership(guestPhone)}
                          disabled={guestChecking || phoneDigits(guestPhone).length < 7}
                          sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
                        >
                          Buscar
                        </Button>
                      </Stack>

                      {guestLookup === 'found' && !guestEditing && (
                        <Box sx={{ border: '1px solid #C8E6C9', bgcolor: '#F1F8E9', borderRadius: 2, p: 1.5 }}>
                          <Typography variant="body2" fontWeight={700}>{guestName}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {formatPhoneDisplay(guestPhone)}{guestEmail ? ` · ${guestEmail}` : ''}
                            {guestLicense ? ` · Lic. ${guestLicense}` : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Ya está en el sistema — no tienes que llenar sus datos.{' '}
                            <Box component="span" onClick={() => setGuestEditing(true)} sx={{ color: '#d40100', cursor: 'pointer', textDecoration: 'underline' }}>
                              Corregir datos
                            </Box>
                          </Typography>
                          {selectedCategory?.requiresLicense && !guestLicense.trim() && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              No tiene licencia de armas registrada — escríbela con "Corregir datos".
                            </Alert>
                          )}
                        </Box>
                      )}

                      {(guestLookup === 'not_found' || guestEditing) && (
                        <>
                          {guestLookup === 'not_found' && (
                            <Typography variant="caption" color="text.secondary">
                              No está en el sistema — llena sus datos:
                            </Typography>
                          )}
                          <TextField
                            label="Nombre completo del invitado"
                            value={guestName}
                            onChange={(e) => setGuestName(e.target.value)}
                            fullWidth
                            required
                            size="small"
                          />
                          <TextField
                            label="Email del invitado (para su confirmación)"
                            value={guestEmail}
                            onChange={(e) => setGuestEmail(e.target.value)}
                            fullWidth
                            size="small"
                            type="email"
                            placeholder="opcional"
                            helperText="Si lo pones, el invitado también recibe el email de confirmación"
                          />
                          <TextField
                            label={selectedCategory?.requiresLicense ? 'Licencia de armas del invitado (requerida)' : 'Licencia de armas del invitado (opcional)'}
                            value={guestLicense}
                            onChange={(e) => setGuestLicense(e.target.value)}
                            fullWidth
                            size="small"
                            required={!!selectedCategory?.requiresLicense}
                            error={selectedCategory?.requiresLicense && !guestLicense.trim()}
                          />
                        </>
                      )}
                      {guestIsMember === false && (
                        <Alert severity="warning">
                          {guestName.trim() || 'El invitado'} no es miembro — se cobra el precio
                          regular ({formatPrice(selectedCategory.priceNonMemberCents)}). Tú realizas el pago.
                        </Alert>
                      )}
                      {guestIsMember === true && (
                        <Alert severity="success">
                          {guestName.trim() || 'El invitado'} es miembro activo — paga precio de miembro
                          ({formatPrice(selectedCategory.priceMemberCents)}).
                        </Alert>
                      )}
                    </Stack>
                  </Collapse>
                </Card>

              </Grid>

              <Grid item xs={12} md={5}>
                <Box sx={{ position: 'sticky', top: 20 }}>
                {/* Oferta de membresía — solo si quien tira no es miembro y hay ahorro */}
                {!effectiveIsMember && !forOther
                  && selectedCategory.priceMemberCents < selectedCategory.priceNonMemberCents && (
                  <Card sx={{ p: 2.5, borderRadius: 3, mb: 2, border: '2px solid #d40100', bgcolor: '#FFF5F5' }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#d40100' }}>
                      Hazte miembro y ahorra
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Estás pagando la tarifa de no-miembro ({formatPrice(selectedCategory.priceNonMemberCents)}).
                      Los miembros pagan {selectedCategory.priceMemberCents === 0 ? 'GRATIS' : formatPrice(selectedCategory.priceMemberCents)} en esta categoría.
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      sx={{ mt: 1.5, fontWeight: 700 }}
                      onClick={() => window.open('/store', '_blank')}
                    >
                      Ver membresías
                    </Button>
                  </Card>
                )}

                {/* Payment Method — only shown when charge > $0 */}
                {chargeAmount > 0 && (
                <Card sx={{ p: 3, borderRadius: 3, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Método de Pago</Typography>
                  {paymentMethods.length > 0 && (
                    <RadioGroup value={selectedPM} onChange={(e) => setSelectedPM(e.target.value)}>
                      {paymentMethods.map((pm) => (
                        <FormControlLabel
                          key={pm.id}
                          value={pm.id}
                          control={<Radio />}
                          label={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <PaymentIcon fontSize="small" />
                              <Typography variant="body2">{(pm.card?.brand || 'Card').toUpperCase()} •••• {pm.card?.last4}</Typography>
                            </Stack>
                          }
                          sx={{ border: selectedPM === pm.id ? '1px solid #d40100' : '1px solid #eee', borderRadius: 2, px: 1, mb: 1 }}
                        />
                      ))}
                    </RadioGroup>
                  )}

                  {/* Tarjeta nueva — siempre visible. El toggle decide si se
                      guarda (apagado = se guarda al pagar). */}
                  <Box sx={{ mt: paymentMethods.length > 0 ? 2 : 0 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      {paymentMethods.length > 0 ? 'O paga con otra tarjeta' : 'Tarjeta'}
                    </Typography>
                    {/* Campos separados: en móvil el de una sola línea se
                        encogía hasta solapar la fecha con el CVC. */}
                    <Box
                      id="stripe-card-number"
                      sx={{
                        border: '1px solid #ccc',
                        borderRadius: 2,
                        p: 1.75,
                        mb: 1,
                        minHeight: 44,
                        bgcolor: '#FAFAFA',
                      }}
                    />
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      <Box
                        id="stripe-card-expiry"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          border: '1px solid #ccc',
                          borderRadius: 2,
                          p: 1.75,
                          minHeight: 44,
                          bgcolor: '#FAFAFA',
                        }}
                      />
                      <Box
                        id="stripe-card-cvc"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          border: '1px solid #ccc',
                          borderRadius: 2,
                          p: 1.75,
                          minHeight: 44,
                          bgcolor: '#FAFAFA',
                        }}
                      />
                    </Stack>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={dontSaveCard}
                          onChange={(e) => setDontSaveCard(e.target.checked)}
                          color="error"
                        />
                      }
                      label={<Typography variant="body2" fontWeight={600}>No guardar esta tarjeta</Typography>}
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      {dontSaveCard
                        ? 'La tarjeta se usa solo para este pago.'
                        : 'Al pagar, tu tarjeta queda guardada para próximas reservas.'}
                    </Typography>
                  </Box>
                </Card>
                )}

                <Card sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Total</Typography>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">{selectedCategory.name}</Typography>
                    <Typography variant="body2">{formatPrice(price)}</Typography>
                  </Stack>
                  {isMember && (
                    <Chip label="Precio de miembro aplicado" size="small" sx={{ bgcolor: '#E8F5E9', color: '#28a745', mb: 1 }} />
                  )}

                  {/* Cupón de descuento */}
                  <Box sx={{ mt: 1.5 }}>
                    {coupon ? (
                      <Alert severity="success" onClose={resetCoupon} sx={{ alignItems: 'center' }}>
                        Cupón <b>{coupon.code}</b> aplicado:{' '}
                        {coupon.discountType === 'percent'
                          ? `${coupon.discountValue}% de descuento`
                          : `${formatPrice(couponDiscount)} de descuento`}
                      </Alert>
                    ) : (
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          label="¿Tienes un cupón?"
                          value={couponInput}
                          onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                          onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                          fullWidth
                          inputProps={{ style: { textTransform: 'uppercase' } }}
                        />
                        <Button
                          variant="outlined"
                          onClick={handleApplyCoupon}
                          disabled={couponChecking || !couponInput.trim()}
                          sx={{ color: '#d40100', borderColor: '#d40100', whiteSpace: 'nowrap', px: 2 }}
                        >
                          {couponChecking ? <CircularProgress size={18} /> : 'Aplicar'}
                        </Button>
                      </Stack>
                    )}
                    {couponError && <Alert severity="warning" sx={{ mt: 1 }}>{couponError}</Alert>}
                  </Box>

                  {couponDiscount > 0 && (
                    <Stack direction="row" justifyContent="space-between" mt={1.5}>
                      <Typography variant="body2" sx={{ color: '#28a745', fontWeight: 600 }}>Descuento ({coupon.code})</Typography>
                      <Typography variant="body2" sx={{ color: '#28a745', fontWeight: 700 }}>-{formatPrice(couponDiscount)}</Typography>
                    </Stack>
                  )}

                  {subtotalPrice > 0 && (
                    <>
                      <Stack direction="row" justifyContent="space-between" mt={1.5}>
                        <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                        <Typography variant="body2">{formatPrice(subtotalPrice)}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" mt={0.5}>
                        <Typography variant="body2" color="text.secondary">IVU 11.5% (10.5% estatal + 1% municipal)</Typography>
                        <Typography variant="body2">{formatPrice(taxAmount)}</Typography>
                      </Stack>
                      {usesDeposit && (
                        <Stack direction="row" justifyContent="space-between" mt={0.5}>
                          <Typography variant="body2" fontWeight="600">Total con impuesto</Typography>
                          <Typography variant="body2" fontWeight="700">{formatPrice(discountedPrice)}</Typography>
                        </Stack>
                      )}
                    </>
                  )}

                  {usesDeposit && (
                    <>
                      <Stack direction="row" justifyContent="space-between" mt={1}>
                        <Typography variant="body2" color="text.secondary">Depósito (cobro ahora)</Typography>
                        <Typography variant="body2" fontWeight="700">{formatPrice(chargeAmount)}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" mt={0.5}>
                        <Typography variant="body2" color="text.secondary">Restante (pago en persona)</Typography>
                        <Typography variant="body2">{formatPrice(remainingBalance)}</Typography>
                      </Stack>
                    </>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" justifyContent="space-between" mb={usesDeposit ? 1 : 3}>
                    <Typography variant="h6" fontWeight="bold">{usesDeposit ? 'Cobro ahora' : 'Total'}</Typography>
                    <Typography variant="h5" fontWeight="bold" color="#d40100">{formatPrice(chargeAmount)}</Typography>
                  </Stack>

                  {usesDeposit && (
                    <Box sx={{
                      bgcolor: '#FFF3E0',
                      border: '2px solid #FF9800',
                      borderRadius: 2,
                      p: 2,
                      mb: 3,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                    }}>
                      <InfoOutlinedIcon sx={{ color: '#E65100', fontSize: 28, mt: 0.25 }} />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#E65100', mb: 0.5 }}>
                          Balance pendiente: {formatPrice(remainingBalance)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#BF360C' }}>
                          {selectedCategory?.depositMessage || `El restante de ${formatPrice(remainingBalance)} se debe pagar en persona el día de la reservación.`}
                        </Typography>
                        {payingPeople > 1 && (
                          <Typography variant="body2" sx={{ color: '#BF360C', mt: 0.5 }}>
                            El depósito es de {formatPrice(selectedCategory.depositCents)} por persona
                            ({payingPeople} personas = {formatPrice(depositAmount)}).
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={processing ? null : <LockIcon />}
                    onClick={handleConfirmBooking}
                    disabled={processing}
                    sx={{ bgcolor: '#d40100', '&:hover': { bgcolor: '#b00100' }, py: 1.5 }}
                  >
                    {processing ? <CircularProgress size={24} color="inherit" /> : chargeAmount === 0 ? 'Confirmar Reservación' : `Pagar ${usesDeposit ? 'Depósito' : ''} ${formatPrice(chargeAmount)}`}
                  </Button>
                </Card>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Back Button — bottom, full-width, thumb-friendly */}
        {activeStep > 0 && activeStep < 5 && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => setActiveStep(activeStep - 1)}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              color: '#555',
              borderColor: '#ddd',
              borderRadius: 3,
              '&:hover': { borderColor: '#bbb', backgroundColor: '#f5f5f5' },
            }}
          >
            Atrás
          </Button>
        )}

        <Box sx={{ pb: 4 }} />
      </Container>
    </PageWrapper>
  );
}

function DateStepContent({ selectedCategory, price, formatPrice, todayStr, onSelectDate }) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [manualDate, setManualDate] = useState('');
  const [allowedDows, setAllowedDows] = useState(null); // null = aún cargando, [] = sin reglas
  const [allowedDates, setAllowedDates] = useState([]); // fechas fijas (cursos de una sola vez)
  const [rulesLoading, setRulesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setRulesLoading(true);
        const dbRef = getFirestore();
        const q = query(
          collection(dbRef, 'availability_rules'),
          where('categoryId', '==', selectedCategory.id)
        );
        const snap = await getDocs(q);
        const dows = new Set();
        const fixedDates = new Set();
        snap.docs.forEach((d) => {
          const r = d.data();
          if (r.active === false) return;
          // Regla de fecha fija (curso que se da una sola vez): habilita ESA
          // fecha, no un día de la semana.
          if (r.date) fixedDates.add(r.date);
          else if (typeof r.dayOfWeek === 'number') dows.add(r.dayOfWeek);
        });
        if (!cancelled) {
          setAllowedDows(Array.from(dows));
          setAllowedDates(Array.from(fixedDates));
        }
      } catch (e) {
        if (!cancelled) { setAllowedDows([]); setAllowedDates([]); }
      } finally {
        if (!cancelled) setRulesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCategory.id]);

  // Construir hasta 60 días buscando los que matchean los dayOfWeek permitidos
  // o una fecha fija. Mostramos los primeros 7 con coincidencia.
  const upcoming = [];
  const hasDows = allowedDows && allowedDows.length > 0;
  const hasFixed = allowedDates && allowedDates.length > 0;
  if (hasDows || hasFixed) {
    for (let i = 0; i < 60 && upcoming.length < 7; i += 1) {
      const d = new Date();
      d.setHours(12, 0, 0, 0);
      d.setDate(d.getDate() + i);
      const iso = d.toISOString().split('T')[0];
      const matches = (hasDows && allowedDows.includes(d.getDay()))
        || (hasFixed && allowedDates.includes(iso));
      if (matches) {
        const dayName = d.toLocaleDateString('es-PR', { weekday: 'short' });
        const dayNum = d.getDate();
        const monthName = d.toLocaleDateString('es-PR', { month: 'short' });
        upcoming.push({ iso, dayName, dayNum, monthName, isToday: i === 0 });
      }
    }
  }

  return (
    <>
      <Typography variant="h5" fontWeight="bold" gutterBottom>Selecciona una Fecha</Typography>
      <Card sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip label={selectedCategory.name} color="primary" />
          <Typography variant="body2" color="text.secondary">{selectedCategory.durationMinutes} min — {formatPrice(price)}</Typography>
        </Stack>
        {selectedCategory.imageUrl && (
          <Box
            component="img"
            src={selectedCategory.imageUrl}
            alt={selectedCategory.name}
            sx={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 2, mt: 2 }}
          />
        )}
        {selectedCategory.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
            {selectedCategory.description}
          </Typography>
        )}
      </Card>
      <Card sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Próximas fechas disponibles</Typography>
        {rulesLoading ? (
          <Stack direction="row" spacing={1.5} sx={{ pb: 1, mt: 1 }}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" width={80} height={84} animation="wave" sx={{ borderRadius: 2 }} />
            ))}
          </Stack>
        ) : (allowedDows && allowedDows.length === 0 && !hasFixed) ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Esta categoría aún no tiene horarios configurados. Contacta al negocio.
          </Alert>
        ) : upcoming.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>No hay fechas próximas disponibles.</Alert>
        ) : (
        <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1, mt: 1 }}>
          {upcoming.map((day) => (
            <Box
              key={day.iso}
              onClick={() => onSelectDate(day.iso)}
              sx={{
                minWidth: 80,
                py: 1.5,
                px: 1,
                borderRadius: 2,
                border: '2px solid transparent',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                bgcolor: day.isToday ? '#FFF5F5' : '#FAFAFA',
                '&:hover': {
                  borderColor: '#d40100',
                  bgcolor: '#FFF5F5',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Typography variant="caption" sx={{ color: '#999', textTransform: 'capitalize', display: 'block', lineHeight: 1.2 }}>
                {day.isToday ? 'Hoy' : day.dayName}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.3, my: 0.25 }}>
                {day.dayNum}
              </Typography>
              <Typography variant="caption" sx={{ color: '#999', textTransform: 'capitalize', display: 'block', lineHeight: 1.2 }}>
                {day.monthName}
              </Typography>
            </Box>
          ))}
        </Stack>
        )}

        <Divider sx={{ my: 2.5 }} />

        {!showCalendar ? (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<CalendarTodayIcon />}
            onClick={() => setShowCalendar(true)}
            sx={{ color: '#666', borderColor: '#ddd' }}
          >
            Ver más fechas
          </Button>
        ) : (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Selecciona otra fecha</Typography>
            <TextField
              fullWidth
              type="date"
              value={manualDate}
              onChange={(e) => setManualDate(e.target.value)}
              inputProps={{ min: todayStr }}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              fullWidth
              variant="contained"
              disabled={!manualDate || manualDate < todayStr}
              onClick={() => onSelectDate(manualDate)}
              sx={{ mt: 1.5, py: 1.3, borderRadius: 2, fontWeight: 600, bgcolor: '#d40100', '&:hover': { bgcolor: '#b30000' } }}
            >
              Confirmar fecha
            </Button>
          </>
        )}
      </Card>
    </>
  );
}
